import React, { useState, useEffect } from 'react';
import { getUserCourses } from '../utils/emailEncoder';
import { formatVND } from '../utils/priceFormatter';
import theme from '../config/theme';
import CourseDetail from './CourseDetail';
import CertifiedTestersPage from './CertifiedTestersPage';
import CertificateDetail from './CertificateDetail';
import TestimonialsPage from './TestimonialsPage';

interface Course {
  id: string;
  name: string;
  code: string;
  description: string;
  color: string;
  videoCount: number;
  type: 'video' | 'zoom-recorded';
  instructor: string;
  duration: string;
  level: string;
  price: string;
  discountPrice?: string;
  endDiscountDate?: string;
  rating: number;
  students: number;
  whatYoullLearn?: string[];
  additionalDescription?: string;
  courseInfoBox?: {
    title: string;
    description: string;
    links?: Array<{
      text: string;
      url: string;
    }>;
  };
  instructorProfile?: {
    title: string;
    experience: string;
    education: string;
    certification: string;
    linkedin: string;
    github: string;
    image?: string;
  };
}

// Optional: lessons count derived from course content data
const lessonsCountByCourseId: Record<string, number> = {
  'playwright-k17': 32,
};

// Helper function to check if discount is still valid
const isDiscountValid = (course: Course): boolean => {
  if (!course.discountPrice || !course.endDiscountDate) {
    return false;
  }
  const endDate = new Date(course.endDiscountDate).getTime();
  const now = new Date().getTime();
  return endDate > now;
};

// Countdown Timer Component
const CountdownTimer: React.FC<{ endDate: string; color: string; compact?: boolean }> = ({ endDate, color, compact = false }) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const end = new Date(endDate).getTime();
      const now = new Date().getTime();
      const difference = end - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft(null);
      }
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [endDate]);

  if (!timeLeft) return null;

  // Show all time units including seconds on both mobile and desktop
  const timeUnits = [
    { value: timeLeft.days, label: 'Days', short: 'D' },
    { value: timeLeft.hours, label: 'Hours', short: 'H' },
    { value: timeLeft.minutes, label: 'Minutes', short: 'M' },
    { value: timeLeft.seconds, label: 'Seconds', short: 'S' }
  ];

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: isMobile && compact ? '6px' : '8px',
      padding: isMobile && compact ? '8px 10px' : '12px 16px',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
      borderRadius: '12px',
      border: `1.5px solid ${color}30`,
      marginBottom: compact ? '0' : '16px',
      position: 'relative',
      overflow: 'hidden',
      flexWrap: 'nowrap',
      minWidth: 0
    }}>
      {/* Animated background pulse */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: `linear-gradient(90deg, transparent, ${color}20, transparent)`,
        animation: 'shimmer 3s infinite',
        pointerEvents: 'none'
      }} />
      <style>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile && compact ? '4px' : '6px',
        position: 'relative',
        zIndex: 1,
        flexShrink: 0
      }}>
        <span style={{ fontSize: isMobile && compact ? '14px' : '18px', flexShrink: 0 }}>⏰</span>
        <span style={{
          fontSize: isMobile && compact ? '10px' : '12px',
          fontWeight: '600',
          color: '#64748b',
          letterSpacing: '0.2px',
          whiteSpace: 'nowrap',
          flexShrink: 0
        }}>
          {isMobile ? 'Sale ends' : 'Sale ends in:'}
        </span>
      </div>

      <div style={{
        display: 'flex',
        gap: isMobile && compact ? '2px' : '6px',
        position: 'relative',
        zIndex: 1,
        flexWrap: 'nowrap',
        flex: 1,
        justifyContent: 'flex-start',
        minWidth: 0,
        overflow: 'visible'
      }}>
        {timeUnits.map((unit, index) => (
          <div key={index} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: isMobile && compact ? '28px' : '40px',
            width: isMobile && compact ? '28px' : 'auto',
            padding: isMobile && compact ? '4px 4px' : '6px 8px',
            background: 'white',
            borderRadius: '6px',
            boxShadow: `0 2px 6px ${color}20`,
            border: `1px solid ${color}20`,
            flexShrink: 0,
            boxSizing: 'border-box'
          }}>
            <span style={{
              fontSize: isMobile && compact ? '12px' : '18px',
              fontWeight: '700',
              color: color,
              lineHeight: '1.1'
            }}>
              {String(unit.value).padStart(2, '0')}
            </span>
            <span style={{
              fontSize: isMobile && compact ? '6px' : '9px',
              fontWeight: '600',
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.2px',
              marginTop: '1px',
              lineHeight: '1'
            }}>
              {unit.short}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface CoursesPageProps {
  user: {
    name: string;
    email: string;
    picture?: string;
  } | null;
  onSignOut: () => void;
  onCourseSelect: (courseId: string) => void;
  onSignIn: () => void;
  userCourses?: string[];
  initialSelectedCourseId?: string;
}

const CoursesPage: React.FC<CoursesPageProps> = ({ user, onSignOut, onCourseSelect, onSignIn, userCourses = [], initialSelectedCourseId }) => {
  const [userCoursesList, setUserCoursesList] = useState<string[]>([]);
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUserCoursesLoading, setIsUserCoursesLoading] = useState(false);
  const [filterMode, setFilterMode] = useState<'all' | 'assigned'>('all');
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [currentPage, setCurrentPage] = useState<'courses' | 'certified-testers' | 'certificate-detail' | 'testimonials'>('courses');
  const [selectedCertId, setSelectedCertId] = useState<string | null>(null);

  const parseHash = () => {
    const hash = window.location.hash;

    // Check for testimonials
    if (hash === '#/testimonials') {
      return { page: 'testimonials', param: null };
    }

    // Check for certificate detail
    const certMatch = hash.match(/^#\/certified-testers\/(.+)$/);
    if (certMatch) {
      return { page: 'certificate-detail', param: certMatch[1] };
    }

    // Check for certified testers list
    if (hash === '#/certified-testers') {
      return { page: 'certified-testers', param: null };
    }

    // Check for course detail
    const courseMatch = hash.match(/^#\/courses\/(.+)$/);
    if (courseMatch) {
      return { page: 'courses', param: courseMatch[1] };
    }

    // Default to courses list
    return { page: 'courses', param: null };
  };

  useEffect(() => {
    const { page, param } = parseHash();

    if (page === 'testimonials') {
      setCurrentPage('testimonials');
      setSelectedCertId(null);
      setSelectedCourse(null);
    } else if (page === 'certificate-detail' && param) {
      setCurrentPage('certificate-detail');
      setSelectedCertId(param);
      setSelectedCourse(null);
    } else if (page === 'certified-testers') {
      setCurrentPage('certified-testers');
      setSelectedCertId(null);
      setSelectedCourse(null);
    } else {
      setCurrentPage('courses');
      setSelectedCertId(null);

      if (param && allCourses.length > 0) {
        const found = allCourses.find(c => c.id === param);
        if (found) {
          setSelectedCourse(found);
        } else {
          setSelectedCourse(null);
        }
      } else {
        setSelectedCourse(null);
      }
    }
  }, [initialSelectedCourseId, allCourses]);

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const { page, param } = parseHash();

      if (page === 'testimonials') {
        setCurrentPage('testimonials');
        setSelectedCertId(null);
        setSelectedCourse(null);
      } else if (page === 'certificate-detail' && param) {
        setCurrentPage('certificate-detail');
        setSelectedCertId(param);
        setSelectedCourse(null);
      } else if (page === 'certified-testers') {
        setCurrentPage('certified-testers');
        setSelectedCertId(null);
        setSelectedCourse(null);
      } else {
        setCurrentPage('courses');
        setSelectedCertId(null);

        if (param && allCourses.length > 0) {
          const found = allCourses.find(c => c.id === param);
          if (found) {
            setSelectedCourse(found);
          } else {
            setSelectedCourse(null);
          }
        } else {
          setSelectedCourse(null);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [allCourses]);

  // Fetch courses and user access on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Small delay to ensure backend is ready
        await new Promise(resolve => setTimeout(resolve, 100));

        // Fetch all courses
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

        const coursesResponse = await fetch(`${API_BASE_URL}/api/courses`);
        const coursesData = await coursesResponse.json();
        setAllCourses(coursesData.courses || []);

        // Only fetch user courses if user is authenticated
        if (user?.email) {
          setIsUserCoursesLoading(true);
          try {
            const userCourses = await getUserCourses(user.email);
            setUserCoursesList(userCourses);
          } catch (error) {
            console.error('Error fetching user courses:', error);
            setUserCoursesList([]);
          } finally {
            setIsUserCoursesLoading(false);
          }
        } else {
          setUserCoursesList([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setAllCourses([]);
        setUserCoursesList([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user?.email]);

  // Use the fetched courses or fallback to prop
  const effectiveUserCourses = userCoursesList.length > 0 ? userCoursesList : userCourses;

  // Handle course selection
  const handleCourseClick = (course: Course) => {
    // Allow all users to view course details
    setSelectedCourse(course);
    // Update URL hash for sharing
    window.location.hash = `#/courses/${course.id}`;
  };

  // Handle back to courses
  const handleBackToCourses = () => {
    setSelectedCourse(null);
    // Clear URL hash when going back to course list
    window.location.hash = '#/courses';
  };

  // Filter courses based on current filter mode and authentication status
  const filteredCourses = allCourses.filter(course => {
    // If user is not authenticated, only show video courses
    if (!user) {
      return course.type === 'video';
    }

    // If user is authenticated, apply normal filtering
    if (filterMode === 'all') {
      // Show all video courses + zoom-recorded courses they have access to
      if (course.type === 'video') return true;
      return effectiveUserCourses.includes(course.id);
    } else {
      // Show only assigned courses (zoom-recorded courses they have access to)
      return effectiveUserCourses.includes(course.id);
    }
  });

  // If showing testimonials page
  if (currentPage === 'testimonials') {
    return (
      <TestimonialsPage
        user={user}
        onSignOut={onSignOut}
        onSignIn={onSignIn}
      />
    );
  }

  // If showing certified testers page
  if (currentPage === 'certified-testers') {
    return (
      <CertifiedTestersPage
        user={user}
        onSignOut={onSignOut}
        onSignIn={onSignIn}
      />
    );
  }

  // If showing certificate detail
  if (currentPage === 'certificate-detail' && selectedCertId) {
    return (
      <CertificateDetail
        certId={selectedCertId}
        user={user}
        onSignOut={onSignOut}
        onSignIn={onSignIn}
        onBack={() => window.location.hash = '#/certified-testers'}
      />
    );
  }

  // If a course is selected, show the course detail page
  if (selectedCourse) {
    const hasAccess = user ? effectiveUserCourses.includes(selectedCourse.id) : false;
    return (
      <CourseDetail
        course={selectedCourse}
        onBack={handleBackToCourses}
        onGoToCourse={onCourseSelect}
        hasAccess={hasAccess}
        user={user}
        onSignIn={onSignIn}
      />
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${theme.colors.bg} 0%, ${theme.colors.bgAlt} 100%)`,
      padding: '0'
    }}>
      <style>
        {`
          @media (max-width: 480px) {
            .filter-toggle-container {
              width: 100% !important;
              padding: 4px !important;
            }
            .filter-toggle-container button {
              flex: 1 !important;
              padding: 8px 12px !important;
              font-size: 13px !important;
              justify-content: center !important;
            }
          }
        `}
      </style>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%)',
        backgroundSize: '200% 200%',
        padding: 'clamp(60px, 10vw, 100px) 16px clamp(80px, 12vw, 120px)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Background Gradient */}
        <style>{`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, #7b5cb8 0%, #6d65d3 50%, #6a70e3 100%)',
          opacity: 1
        }} />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: '900px', margin: '0 auto', padding: '0 16px' }}>
          <h1 style={{
            margin: '0 0 24px 0',
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: '800',
            color: '#ffffff',
            lineHeight: '1.2',
            letterSpacing: '-0.02em'
          }}>
            Master Your Automation
            <br />
            Testing Skills
          </h1>
          <p style={{
            margin: '0 0 40px 0',
            fontSize: 'clamp(1rem, 2vw, 1.125rem)',
            color: 'rgba(255,255,255,0.9)',
            fontWeight: '400',
            lineHeight: '1.6',
            maxWidth: '600px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            {user ? (
              <>
                Welcome back, <strong style={{ fontWeight: '600' }}>{user.name}</strong>!
                <br style={{ display: window.innerWidth < 768 ? 'block' : 'none' }} />
                {' '}Continue your learning journey with expert-led training.
              </>
            ) : (
              'Discover quality courses and advance your career with expert-led training. Transform your skills and unlock new opportunities.'
            )}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: 'clamp(40px, 8vw, 80px) clamp(16px, 4vw, 24px)',
        position: 'relative',
        marginTop: 'clamp(-30px, -5vw, -60px)'
      }}>

        {/* Filter Toggle - Only show for authenticated users when not loading */}
        {!isLoading && !isUserCoursesLoading && user && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 'clamp(32px, 6vw, 48px)',
            padding: '0 16px'
          }}>
            <div
              className="filter-toggle-container"
              style={{
                display: 'flex',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '50px',
                padding: '6px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(12px)',
                width: 'auto',
                maxWidth: '100%',
                flexDirection: 'row',
                gap: '0',
                position: 'relative'
              }}>
              <button
                onClick={() => setFilterMode('all')}
                style={{
                  padding: '12px 32px',
                  borderRadius: '40px',
                  border: 'none',
                  background: filterMode === 'all'
                    ? 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)'
                    : 'transparent',
                  color: filterMode === 'all' ? 'white' : '#64748b',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: filterMode === 'all'
                    ? '0 4px 12px rgba(79, 70, 229, 0.3)'
                    : 'none',
                  position: 'relative',
                  whiteSpace: 'nowrap'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: filterMode === 'all' ? 1 : 0.7 }}>
                  <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" />
                </svg>
                All Courses
              </button>

              <button
                onClick={() => setFilterMode('assigned')}
                style={{
                  padding: '12px 32px',
                  borderRadius: '40px',
                  border: 'none',
                  background: filterMode === 'assigned'
                    ? 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)'
                    : 'transparent',
                  color: filterMode === 'assigned' ? 'white' : '#64748b',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: filterMode === 'assigned'
                    ? '0 4px 12px rgba(79, 70, 229, 0.3)'
                    : 'none',
                  position: 'relative',
                  whiteSpace: 'nowrap'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: filterMode === 'assigned' ? 1 : 0.7 }}>
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                My Courses
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '16px',
            padding: '40px 24px',
            marginBottom: '32px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)'
          }}>
            {/* Animated background pattern */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              animation: 'float 6s ease-in-out infinite',
              opacity: 0.3
            }} />

            {/* Main loading content */}
            <div style={{ position: 'relative', zIndex: 2 }}>
              {/* Animated spinner */}
              <div style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 24px',
                position: 'relative'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  border: '4px solid rgba(255, 255, 255, 0.2)',
                  borderTop: '4px solid #ffffff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '32px',
                  height: '32px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '3px solid #ffffff',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite reverse'
                }} />
              </div>

              {/* Loading text with animation */}
              <h3 style={{
                margin: '0 0 12px 0',
                color: '#ffffff',
                fontSize: '24px',
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                Loading Your Courses
                <span style={{
                  animation: 'dots 1.5s infinite',
                  marginLeft: '4px'
                }}>...</span>
              </h3>

              <p style={{
                margin: '0 0 20px 0',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '16px',
                fontWeight: '500'
              }}>
                Please wait while we check your course access
              </p>

              {/* Progress bar */}
              <div style={{
                width: '200px',
                height: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '2px',
                margin: '0 auto',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, #ffffff, #f0f9ff, #ffffff)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 2s ease-in-out infinite',
                  borderRadius: '2px'
                }} />
              </div>
            </div>

            {/* CSS Animations */}
            <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
            @keyframes dots {
              0%, 20% { opacity: 0; }
              50% { opacity: 1; }
              100% { opacity: 0; }
            }
            @keyframes shimmer {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-10px) rotate(180deg); }
            }
          `}</style>
          </div>
        )}

        {/* User Courses Loading State */}
        {!isLoading && isUserCoursesLoading && user && (
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #0ea5e9',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span style={{
                color: '#0369a1',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                Checking your course access...
              </span>
            </div>
            <p style={{
              margin: 0,
              color: '#0284c7',
              fontSize: '14px'
            }}>
              Please wait while we verify your enrolled courses
            </p>
          </div>
        )}

        {/* No Access Message - Only show for authenticated users when not loading AND when filtering by assigned courses */}
        {!isLoading && !isUserCoursesLoading && user && effectiveUserCourses.length === 0 && filterMode === 'assigned' && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#fecaca',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '24px'
            }}>
              🔒
            </div>
            <h3 style={{
              margin: '0 0 8px 0',
              color: '#dc2626',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              No Course Access
            </h3>
            <p style={{
              margin: '0 0 16px 0',
              color: '#991b1b',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Your email ({user.email}) is not enrolled in any courses yet.
            </p>
            <p style={{
              margin: 0,
              color: '#7f1d1d',
              fontSize: '13px'
            }}>
              Please contact your trainer to request access to courses.
            </p>
          </div>
        )}

        {/* Filter Status - Only show for authenticated users when not loading */}
        {!isLoading && !isUserCoursesLoading && user && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '24px',
            padding: '0 8px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#111827',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              {filterMode === 'all' ? 'All Courses' : 'My Courses'}
              <span style={{
                fontSize: '14px',
                color: '#6B7280',
                fontWeight: '500',
                backgroundColor: '#F3F4F6',
                padding: '4px 10px',
                borderRadius: '20px',
                border: '1px solid #E5E7EB'
              }}>
                {filteredCourses.length}
              </span>
            </h2>
          </div>
        )}

        {/* Courses Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(300px, 40vw, 380px), 1fr))',
          gap: 'clamp(20px, 4vw, 32px)',
          marginTop: 'clamp(24px, 5vw, 40px)'
        }}>
          {filteredCourses.map((course) => {
            // Check if user has access to this course
            const hasAccess = user ? effectiveUserCourses.includes(course.id) : false;
            return (
              <div
                key={course.id}
                onClick={() => handleCourseClick(course)}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '24px',
                  padding: '0',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: hasAccess
                    ? `0 8px 32px ${course.color}20, 0 2px 8px rgba(0, 0, 0, 0.08)`
                    : '0 4px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                  border: hasAccess ? `3px solid ${course.color}` : '1px solid #e2e8f0'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                  e.currentTarget.style.boxShadow = hasAccess
                    ? `0 24px 48px ${course.color}30, 0 8px 16px rgba(0, 0, 0, 0.12)`
                    : '0 16px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = hasAccess
                    ? `0 8px 32px ${course.color}20, 0 2px 8px rgba(0, 0, 0, 0.08)`
                    : '0 4px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)';
                }}
              >
                {/* Course Header with Gradient */}
                <div style={{
                  background: `linear-gradient(135deg, ${course.color} 0%, ${course.color}dd 50%, ${course.color}cc 100%)`,
                  padding: 'clamp(20px, 4vw, 28px)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Animated Background Pattern */}
                  <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-20%',
                    width: '150px',
                    height: '150px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(20px)',
                    animation: 'float 6s ease-in-out infinite'
                  }} />
                  <div style={{
                    position: 'absolute',
                    bottom: '-30%',
                    left: '-10%',
                    width: '120px',
                    height: '120px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(15px)',
                    animation: 'float 8s ease-in-out infinite reverse'
                  }} />

                  <style>{`
                    @keyframes float {
                      0%, 100% { transform: translateY(0px) scale(1); opacity: 0.3; }
                      50% { transform: translateY(-20px) scale(1.1); opacity: 0.5; }
                    }
                  `}</style>

                  <div style={{ position: 'relative', zIndex: 2 }}>
                    {/* Course Type Badge */}
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: 'rgba(255,255,255,0.25)',
                      color: 'white',
                      padding: '10px 18px',
                      borderRadius: '24px',
                      fontSize: '12px',
                      fontWeight: '700',
                      marginBottom: '18px',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <span style={{ fontSize: '16px' }}>{course.type === 'video' ? '🎥' : '📺'}</span>
                      {course.type === 'video' ? 'Video Course' : 'Live Recording'}
                    </div>

                    {/* Course Title */}
                    <h3 style={{
                      margin: '0 0 14px 0',
                      fontSize: 'clamp(1.25rem, 3.5vw, 1.75rem)',
                      color: '#ffffff',
                      fontWeight: '800',
                      lineHeight: '1.2',
                      textShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      letterSpacing: '-0.02em'
                    }}>
                      {course.name}
                    </h3>

                    {/* Course Code */}
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      padding: '8px 14px',
                      borderRadius: '14px',
                      fontSize: '11px',
                      fontWeight: '700',
                      letterSpacing: '0.8px',
                      textTransform: 'uppercase',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.25)'
                    }}>
                      {course.code}
                    </div>
                  </div>
                </div>
                {/* Course Content */}
                <div style={{ padding: 'clamp(20px, 4vw, 28px)' }}>
                  {/* Discount Countdown Timer */}
                  {isDiscountValid(course) && (
                    <CountdownTimer endDate={course.endDiscountDate!} color={course.color} compact={false} />
                  )}

                  {/* Course Description */}
                  <p style={{
                    color: '#475569',
                    margin: '0 0 24px 0',
                    lineHeight: '1.7',
                    fontSize: '15px',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontWeight: '400'
                  }}>
                    {course.description}
                  </p>

                  {/* Course Details */}
                  <div style={{
                    marginBottom: 'clamp(20px, 4vw, 24px)',
                    padding: 'clamp(16px, 3vw, 20px)',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                  }}>
                    {/* Instructor and Level Row */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 'clamp(8px, 2vw, 12px)',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px' }}>👨‍🏫</span>
                        <a
                          href="https://www.linkedin.com/in/tu-huynh-van/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#64748b',
                            textDecoration: 'none',
                            fontSize: 'clamp(12px, 2.5vw, 14px)',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = '#667eea'}
                          onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                        >
                          {course.instructor}
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.7 }}>
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                          </svg>
                        </a>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px' }}>📊</span>
                        <span style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#64748b', fontWeight: '500' }}>
                          {course.level}
                        </span>
                      </div>
                    </div>

                    {/* Video Count and Price Row */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px' }}>📹</span>
                        <span style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#64748b', fontWeight: '500' }}>
                          {(lessonsCountByCourseId[course.id] ?? course.videoCount)} videos
                        </span>
                      </div>
                      {course.price && (
                        <div style={{
                          fontSize: 'clamp(14px, 3vw, 16px)',
                          fontWeight: '700',
                          color: course.color,
                          backgroundColor: `${course.color}15`,
                          padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                          borderRadius: '8px',
                          border: `1px solid ${course.color}30`,
                          display: 'flex',
                          gap: '4px',
                          flexDirection: isDiscountValid(course) ? 'column' : 'row',
                          alignItems: isDiscountValid(course) ? 'flex-start' : 'center'
                        }}>
                          {isDiscountValid(course) ? (
                            <>
                              <span style={{
                                textDecoration: 'line-through',
                                fontSize: 'clamp(12px, 2.5vw, 14px)',
                                color: theme.colors.muted,
                                fontWeight: '500'
                              }}>
                                {formatVND(course.price)}
                              </span>
                              <span style={{
                                color: '#ef4444',
                                fontWeight: '700',
                                fontSize: 'clamp(14px, 3vw, 16px)'
                              }}>
                                {formatVND(course.discountPrice)}
                              </span>
                            </>
                          ) : (
                            <span>{formatVND(course.price)}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Access Status */}
                  <div
                    onClick={() => handleCourseClick(course)}
                    style={{
                      display: 'flex',
                      justifyContent: hasAccess ? 'space-between' : 'center',
                      alignItems: 'center',
                      padding: '0',
                      marginTop: '8px',
                      position: 'relative',
                      cursor: 'pointer'
                    }}
                  >
                    {hasAccess && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        padding: '12px 16px',
                        background: `linear-gradient(135deg, ${course.color}08 0%, ${course.color}05 100%)`,
                        borderRadius: '10px',
                        border: `1px solid ${course.color}20`
                      }}>
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: course.color,
                          boxShadow: `0 0 8px ${course.color}40`,
                          animation: 'pulse 2s ease-in-out infinite',
                          flexShrink: 0
                        }} />
                        <span style={{
                          fontSize: '13px',
                          fontWeight: '600',
                          color: course.color,
                          letterSpacing: '0.01em'
                        }}>
                          You have access
                        </span>
                      </div>
                    )}
                    <div 
                      className="view-details-button"
                      style={{
                        fontSize: '15px',
                        fontWeight: '700',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        padding: '14px 28px',
                        borderRadius: '12px',
                        background: hasAccess
                          ? `linear-gradient(135deg, ${course.color} 0%, ${course.color}dd 100%)`
                          : 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #6366F1 100%)',
                        backgroundSize: '200% 200%',
                        border: 'none',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: hasAccess
                          ? `0 4px 16px ${course.color}30, 0 2px 8px ${course.color}20`
                          : '0 4px 16px rgba(99, 102, 241, 0.3), 0 2px 8px rgba(99, 102, 241, 0.2)',
                        position: 'relative',
                        overflow: 'hidden',
                        width: hasAccess ? 'auto' : '100%',
                        minWidth: '160px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                        e.currentTarget.style.boxShadow = hasAccess
                          ? `0 8px 24px ${course.color}40, 0 4px 12px ${course.color}30`
                          : '0 8px 24px rgba(99, 102, 241, 0.4), 0 4px 12px rgba(99, 102, 241, 0.3)';
                        e.currentTarget.style.backgroundPosition = '100% 50%';
                        const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
                        const shimmer = e.currentTarget.querySelector('.shimmer-overlay') as HTMLElement;
                        if (arrow) {
                          arrow.style.transform = 'translateX(4px)';
                        }
                        if (shimmer) {
                          shimmer.style.left = '100%';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = hasAccess
                          ? `0 4px 16px ${course.color}30, 0 2px 8px ${course.color}20`
                          : '0 4px 16px rgba(99, 102, 241, 0.3), 0 2px 8px rgba(99, 102, 241, 0.2)';
                        e.currentTarget.style.backgroundPosition = '0% 50%';
                        const arrow = e.currentTarget.querySelector('.arrow-icon') as HTMLElement;
                        const shimmer = e.currentTarget.querySelector('.shimmer-overlay') as HTMLElement;
                        if (arrow) {
                          arrow.style.transform = 'translateX(0)';
                        }
                        if (shimmer) {
                          shimmer.style.left = '-100%';
                        }
                      }}
                    >
                      {/* Shimmer effect overlay */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                        transition: 'left 0.5s ease'
                      }} 
                      className="shimmer-overlay"
                      />
                      
                      <span style={{ position: 'relative', zIndex: 1 }}>
                        {hasAccess ? 'Continue Learning' : 'View Details'}
                      </span>
                      <svg
                        className="arrow-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: 'translateX(0)',
                          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                      
                      <style>{`
                        .view-details-button:hover .shimmer-overlay {
                          left: 100%;
                        }
                        @keyframes pulse {
                          0%, 100% { opacity: 1; transform: scale(1); }
                          50% { opacity: 0.8; transform: scale(1.1); }
                        }
                      `}</style>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CoursesPage;